<template>
    <Draggable
        v-model="localOrderedSections"
        class="campaigns__draggable-wrapper"
        item-key="element"
        handle=".campaigns__section-drag-handle"
    >
        <template #item="{ element: sectionId }">
            <Collapse :key="sectionId" simple class="hox-collapsible">
                <Panel name="1" class="hox-collapsible__panel">
                    <span data-testid="campaign-section__name">{{ sectionsById[sectionId].name }}</span>
                    <span v-if="userCanManageCampaignSections" class="campaigns__section-action-items">
                        <hox-square-button
                            size="small"
                            data-testid="campaign-section__button--edit"
                            @click="showEditCampaignSectionModal(sectionId)"
                        >
                            <Icon type="md-create" />
                        </hox-square-button>
                    </span>
                    <Icon type="ios-move" class="campaigns__section-drag-handle"></Icon>

                    <template #content>
                        <div class="hox-collapsible__body">
                            <div v-if="!campaignsBySectionId[sectionId]" class="campaigns__section-empty-message">
                                <p>There are no campaigns in this section.</p>
                                <p>
                                    You can add a campaign to this section by editing an existing campaign or creating a
                                    new campaign and selecting this section.
                                </p>
                            </div>

                            <CampaignsList
                                v-else
                                :campaigns="campaignsBySectionId[sectionId]"
                                :client-id="clientId"
                                @duplicate="showDuplicateCampaignModal"
                                @edit="showEditCampaignModal"
                                @manageTeam="showTeam"
                                @setSection="showSetCampaignSectionModal"
                            />
                        </div>
                    </template>
                </Panel>
            </Collapse>
        </template>
    </Draggable>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import Draggable from "vuedraggable";

import CampaignsList from "@/components/CampaignsListing/CampaignsList.vue";
import type { SectionsWithCampaigns } from "@/views/CampaignsDashboard.vue";

export default defineComponent({
    name: "CampaignsListDraggable",
    components: {
        Draggable,
        CampaignsList
    },

    props: {
        orderedSections: {
            type: Array as PropType<string[]>,
            required: true
        },
        sectionsById: {
            type: Object as PropType<Record<string, { name: string }>>,
            required: true
        },
        campaignsBySectionId: {
            type: Object as PropType<SectionsWithCampaigns>,
            required: true
        },
        clientId: {
            type: String,
            required: true
        },
        userCanManageCampaignSections: {
            type: Boolean,
            required: true
        }
    },
    emits: [
        "update:orderedSections",
        "edit-section",
        "duplicate-campaign",
        "edit-campaign",
        "manage-team",
        "set-section"
    ],
    data() {
        return {
            localOrderedSections: [...this.orderedSections]
        };
    },
    watch: {
        localOrderedSections: {
            immediate: true,
            handler(newVal: string[]) {
                this.$emit("update:orderedSections", newVal);
            }
        },

        orderedSections: {
            immediate: true,
            handler(newVal: string[], oldVal?: string[]) {
                if (newVal.length === oldVal?.length && newVal.every((val, index) => val === oldVal[index])) {
                    return;
                }

                this.localOrderedSections = [...newVal];
            }
        }
    },
    methods: {
        showEditCampaignSectionModal(sectionId: string) {
            this.$emit("edit-section", sectionId);
        },
        showDuplicateCampaignModal(campaignId: string) {
            this.$emit("duplicate-campaign", campaignId);
        },
        showEditCampaignModal(campaignId: string) {
            this.$emit("edit-campaign", campaignId);
        },
        showTeam(campaignId: string) {
            this.$emit("manage-team", campaignId);
        },
        showSetCampaignSectionModal(campaignId: string) {
            this.$emit("set-section", campaignId);
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.hox-collapsible {
    width: 100%;

    > .ivu-collapse-item.ivu-collapse-item-active {
        border-bottom: 1px transparent;
    }

    .hox-collapsible__body {
        margin-top: $spacing;
    }
}

.ivu-collapse {
    border-top: 1px transparent;
}

.square-button--default:hover {
    background-color: transparent;
}

.campaigns__section-empty-message {
    color: $cmp-light-font-color;
    font-size: $font-size-base;
    margin: (0 - $spacing-small) 0 $spacing;

    p {
        margin: 0 0 $spacing-small;

        &:first-child {
            font-weight: bold;
        }
    }
}

.campaigns__section-drag-handle {
    float: right;
    margin-top: 16px;
}
</style>
